/**
 * `>` — the command registry as a provider (PLAN §3.1). Replaces the old
 * `switch (actionId)`; shortcut labels come from the remappable registry
 * (config/shortcuts.js), so a remapped key shows its new combo here.
 */
import { Zap, Keyboard, Play } from 'lucide-react';
import { getCommands } from '../registry';
import { selectTop } from '../localSearch';
import { ORDER } from './order';
import { resolveShortcutKeys, shortcutLabelString } from '../../config/shortcuts';
import { usePreferencesStore } from '../../store/preferencesStore';

export function commandShortcutLabel(cmd) {
  if (!cmd.shortcutId) return null;
  const keys = resolveShortcutKeys(cmd.shortcutId, usePreferencesStore.getState().keyboardShortcuts);
  return keys ? shortcutLabelString(keys) : null;
}

function visible(cmd, ctx) {
  try {
    return !cmd.when || !!cmd.when(ctx);
  } catch {
    return false;
  }
}

const resultCache = new WeakMap(); // command -> { t, ks, result }

/** The row for a command; the same object while the language and the key bindings stay. */
export function commandResult(cmd, ctx) {
  const ks = usePreferencesStore.getState().keyboardShortcuts;
  const hit = resultCache.get(cmd);
  if (hit && hit.t === ctx.t && hit.ks === ks) return hit.result;
  const result = buildCommandResult(cmd, ctx);
  resultCache.set(cmd, { t: ctx.t, ks, result });
  return result;
}

function buildCommandResult(cmd, ctx) {
  return {
    provider: 'commands',
    id: cmd.id,
    title: ctx.t(cmd.titleKey),
    icon: cmd.icon || Zap,
    searchFields: (cmd.keywords || []).map((k) => ({ text: k, weight: 0.4 })),
    payload: { commandId: cmd.id, shortcutLabel: commandShortcutLabel(cmd), group: cmd.group || 'general' },
  };
}

/** Root's "Suggested" section. */
export function suggestedCommands(ctx, list = getCommands()) {
  return list.filter((c) => c.group === 'suggested' && visible(c, ctx)).map((c) => commandResult(c, ctx));
}

export default function create({ list = getCommands } = {}) {
  const byId = () => new Map(list().map((c) => [c.id, c]));
  return {
    id: 'commands',
    prefixes: ['cmd', 'command'],
    sigils: ['>'],
    labelKey: 'palette.scope.commands',
    icon: Zap,
    order: ORDER.commands,
    kind: 'local-sync',
    available: () => true,
    search(req) {
      const all = list().filter((c) => visible(c, req.ctx)).map((c) => commandResult(c, req.ctx));
      return selectTop(all, req, { emptyOrder: (a, b) => a.title.localeCompare(b.title) });
    },
    async recent(ids, ctx) {
      const map = byId();
      return ids.map((id) => map.get(id)).filter((c) => c && visible(c, ctx)).map((c) => commandResult(c, ctx));
    },
    actions(result, ctx) {
      const cmd = byId().get(result.payload?.commandId);
      const out = [{ id: 'run', titleKey: 'palette.action.run', icon: Play, run: (_r, c) => cmd?.run(c || ctx) }];
      if (cmd?.shortcutId) {
        out.push({
          id: 'changeShortcut',
          titleKey: 'palette.action.changeShortcut',
          icon: Keyboard,
          run: (_r, c) => (c || ctx).nav.onOpenSettings?.('keyboard'),
        });
      }
      return out;
    },
  };
}
